import { UiButtonComponent } from 'src/app/shared/ui';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MaintenanceService,
  MaintenanceStatus,
  MaintenanceMode,
} from 'src/app/core/services';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss'],
  standalone: true,
  imports: [UiButtonComponent, CommonModule],
})
export class MaintenanceComponent implements OnInit, OnDestroy {
  maintenanceStatus: MaintenanceStatus | null = null;
  maintenanceMode: MaintenanceMode = 'none';
  private statusSubscription?: Subscription;
  private modeSubscription?: Subscription;
  private recoveryTimer?: ReturnType<typeof setInterval>;
  private recoveryStartedAt = 0;
  /** replaced in tests */
  reload = () => window.location.reload();
  /** server restart or unreachable: probe until it answers again, then reload by itself */
  static readonly RECOVERY_STATUSES = [0, 502, 503, 504];
  static readonly RECOVERY_PROBE_MS = 5000;
  static readonly RECOVERY_MAX_MS = 5 * 60 * 1000;

  constructor(private maintenanceService: MaintenanceService) {}

  ngOnInit() {
    this.statusSubscription =
      this.maintenanceService.maintenanceStatus$.subscribe((status) => {
        this.maintenanceStatus = status;
        this.updateRecovery();
      });

    this.modeSubscription = this.maintenanceService.maintenanceMode$.subscribe(
      (mode) => {
        this.maintenanceMode = mode;
        this.updateRecovery();
      }
    );
  }

  /** A deploy or restart answers 502/503 for a minute or two: no reason for a dead end. */
  get autoRecovering(): boolean {
    return !!this.recoveryTimer;
  }

  private updateRecovery() {
    const status = this.maintenanceStatus?.diagnostic?.status;
    const recoverable = this.isServerError && status !== undefined &&
      MaintenanceComponent.RECOVERY_STATUSES.includes(status);
    if (!recoverable) return this.stopRecovery();
    if (this.recoveryTimer) return;
    this.recoveryStartedAt = Date.now();
    this.recoveryTimer = setInterval(() => void this.probe(), MaintenanceComponent.RECOVERY_PROBE_MS);
  }

  private async probe() {
    if (Date.now() - this.recoveryStartedAt > MaintenanceComponent.RECOVERY_MAX_MS) return this.stopRecovery();
    try {
      const res = await fetch(`${environment.API_SERVER_URL}/user/auth/is-logged-in?_=${Date.now()}`, { credentials: 'include' });
      if (res.ok) {
        this.stopRecovery();
        this.reload();
      }
    } catch {
      // still unreachable: next probe
    }
  }

  private stopRecovery() {
    if (this.recoveryTimer) clearInterval(this.recoveryTimer);
    this.recoveryTimer = undefined;
  }

  ngOnDestroy() {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    if (this.modeSubscription) {
      this.modeSubscription.unsubscribe();
    }
    this.stopRecovery();
  }

  onRetryClick() {
    window.location.reload();
  }

  get isServerError(): boolean {
    return this.maintenanceMode === 'server_error';
  }

  get isPlannedMaintenance(): boolean {
    return this.maintenanceMode === 'planned';
  }

  get mainTitle(): string {
    if (this.isServerError) {
      return this.maintenanceStatus?.diagnostic?.canSignIn
        ? 'Bitte erneut anmelden' : 'Das hat leider nicht geklappt';
    }
    return 'Karten werden gemischt';
  }

  get displayMessage(): string {
    if (this.isServerError) {
      return (
        this.maintenanceStatus?.message ||
        'Es treten gerade unerwartete Serverfehler auf. Wir arbeiten daran, das Problem zu beheben.'
      );
    }
    return (
      this.maintenanceStatus?.message ||
      'Das Schnapsen-Spiel wird gerade vorbereitet!'
    );
  }

  get subMessage(): string {
    if (this.isServerError) {
      return 'Bitte versuche es in wenigen Minuten erneut. Sollte das Problem bestehen bleiben, kontaktiere uns.';
    }
    return 'Wir mischen die Karten und bereiten den Spieltisch vor. In wenigen Minuten kannst du wieder spielen.';
  }

  get updatedAt(): string | null {
    if (!this.maintenanceStatus?.updated_at) return null;
    return new Date(this.maintenanceStatus.updated_at).toLocaleString('de-AT');
  }
}
